import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { useAiGeneratedFields } from '../../../../hooks/queries/useAiGeneratedFields';
import { useGetPropertyFields } from '../../../../hooks/queries/useGetPropertyFields';

import AiDraftFailedModal from './AiDraftFailedModal';

import { useWizardNavigation } from '../../WizardNavigationProvider';

/**
 * Mounted once for the whole wizard rather than on the step that shows the draft, so a failed
 * generate reaches the landlord on whichever step they are on when it lands.
 */
const AiDraftFailureGate = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { status, draftNotes } = useAiGeneratedFields();
  const { mutate: getPropertyFields, isPending: isRetryingDraft } = useGetPropertyFields();
  const { optOutOfAiListing } = useWizardNavigation();

  const [isDismissed, setIsDismissed] = useState(false);

  // Scoped to the failure it was dismissed for, so a later one still reaches the landlord.
  useEffect(() => {
    if (status !== 'failed') setIsDismissed(false);
  }, [status]);

  useEffect(() => {
    setIsDismissed(false);
  }, [listingAlias]);

  // Re-fires the same request the first step made. The photos already sit on the listing, so the
  // notes are all a retry needs, and the shared mutation key puts the steps back into waiting.
  const handleRetry = draftNotes
    ? () => getPropertyFields({ postData: { listingAlias, text: draftNotes } })
    : undefined;

  const handleCompleteManually = () => {
    setIsDismissed(true);
    optOutOfAiListing?.();
  };

  // X / backdrop / Esc retries; with no cached notes it falls through to the manual opt-out.
  const handleDismiss = () => {
    if (isRetryingDraft) return;

    if (handleRetry) {
      handleRetry();
      return;
    }

    handleCompleteManually();
  };

  return (
    <AiDraftFailedModal
      isOpen={status === 'failed' && !isDismissed}
      onDismiss={handleDismiss}
      onCompleteManually={handleCompleteManually}
      onRetry={handleRetry}
      isRetrying={isRetryingDraft}
    />
  );
};

export default AiDraftFailureGate;
